import { useRef, useState } from 'react';
import { Loader2, BadgeCheck, Building2, CheckCircle, RefreshCw, Unlink, ExternalLink, Camera, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../lib/api';
import { BACKEND_URL } from '../../lib/backend';
import { toast } from 'sonner';

const AVAILABLE_GENRES = [
  { value: 'dancehall', label: 'Dancehall' },
  { value: 'reggae', label: 'Reggae' },
  { value: 'soca', label: 'Soca' },
  { value: 'afrobeats', label: 'Afrobeats' },
  { value: 'hiphop', label: 'Hip Hop' },
  { value: 'rnb', label: 'R&B' },
  { value: 'gospel', label: 'Gospel' },
  { value: 'other', label: 'Other' }
];

/** Circular preview + device upload + URL fallback for the artist photo. */
function ProfilePhotoSection({ profileForm, setProfileForm }) {
  const photoInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const previewSrc = profileForm.image_url
    ? (profileForm.image_url.startsWith('/') ? `${BACKEND_URL}${profileForm.image_url}` : profileForm.image_url)
    : null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.uploadArtistProfilePhoto(fd);
      setProfileForm((prev) => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload that image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>Profile Photo</Label>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#1a1a1a] border-2 border-[#262626] overflow-hidden flex items-center justify-center shrink-0" data-testid="edit-photo-preview">
          {previewSrc ? (
            <img src={previewSrc} alt="Profile preview" className="w-full h-full object-cover" />
          ) : (
            <User className="w-7 h-7 text-[#555]" />
          )}
        </div>
        <div className="space-y-1">
          <Button
            type="button" variant="outline" size="sm"
            className="border-[#404040] hover:border-lime"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploading}
            data-testid="edit-photo-upload-btn"
          >
            {uploading ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading…</>) : (<><Camera className="w-4 h-4 mr-2" />Upload a photo</>)}
          </Button>
          <p className="text-xs text-[#666]">JPEG, PNG, WebP or GIF — up to 5MB</p>
        </div>
        <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="edit-photo-input" />
      </div>
      <div className="pt-1">
        <Label htmlFor="edit-image" className="text-xs text-[#A1A1AA]">…or paste a link to your photo</Label>
        <Input id="edit-image" value={profileForm.image_url} onChange={(e) => setProfileForm({ ...profileForm, image_url: e.target.value })} className="input-dark mt-1.5" placeholder="https://..." data-testid="edit-image-input" />
      </div>
    </div>
  );
}

function VerificationBadge({ profile, handleRequestVerification, requestingVerification }) {
  if (!profile || profile.is_verified) return null;
  return (
    <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
      <div className="flex items-start gap-3">
        <BadgeCheck className="w-5 h-5 text-blue-400 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-blue-400">Get Verified</p>
          <p className="text-sm text-[#A1A1AA] mb-3">Verified artists get a badge and increased visibility</p>
          <Button onClick={handleRequestVerification} disabled={requestingVerification || profile.verification_requested} size="sm" variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10" data-testid="request-verification-btn">
            {requestingVerification && <Loader2 className="w-3 h-3 animate-spin mr-2" />}
            {profile.verification_requested ? 'Verification Pending' : 'Request Verification'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BankSection({ connectStatus, connectLoading, handleConnectBank, handleDisconnectBank, disconnecting }) {
  if (connectStatus?.connected) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 p-3 bg-lime/5 border border-lime/20 rounded-lg">
          <Building2 className="w-5 h-5 text-lime" />
          <div className="flex-1">
            <p className="text-sm font-medium text-lime">Bank Connected</p>
            <p className="text-xs text-[#A1A1AA]">Withdrawals go directly to your bank</p>
          </div>
          <CheckCircle className="w-4 h-4 text-lime" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="border-[#333]" onClick={() => handleConnectBank(true)} disabled={connectLoading} data-testid="edit-update-bank-btn">
            {connectLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}Update
          </Button>
          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={handleDisconnectBank} disabled={disconnecting} data-testid="edit-disconnect-bank-btn">
            {disconnecting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Unlink className="w-3 h-3 mr-1" />}Disconnect
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-[#A1A1AA]">Connect your bank for automatic withdrawals</p>
      <Button onClick={() => handleConnectBank(connectStatus?.status === 'pending')} disabled={connectLoading} className="bg-lime text-black hover:bg-lime/90 font-bold" data-testid="edit-connect-bank-btn">
        {connectLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Building2 className="w-4 h-4 mr-2" />}
        {connectStatus?.status === 'pending' ? 'Continue Setup' : 'Connect Bank Account'}
        <ExternalLink className="w-3 h-3 ml-2" />
      </Button>
      <p className="text-xs text-[#555]">Powered by Stripe. Your bank details are never shared with Tune4Tune.</p>
    </div>
  );
}

function PaymentMethodSection({ paymentMethod, setPaymentMethod, paymentData, setPaymentData, handleSavePaymentMethod, savingPayment }) {
  return (
    <div className="pt-4 border-t border-[#262626]">
      <Label className="mb-3 block font-semibold">Alternative Payout Methods</Label>
      <div className="flex gap-2 mb-3">
        {['paypal', 'zelle', 'bank_transfer'].map((m) => (
          <button key={m} onClick={() => setPaymentMethod(m)} className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${paymentMethod === m ? 'border-lime bg-lime/10 text-lime' : 'border-[#333] text-[#A1A1AA] hover:border-[#555]'}`} data-testid={`edit-pay-${m}`}>
            {m === 'paypal' ? 'PayPal' : m === 'zelle' ? 'Zelle' : 'Bank Transfer'}
          </button>
        ))}
      </div>
      {paymentMethod === 'paypal' && (
        <div className="space-y-2">
          <Label htmlFor="edit-paypal-email">PayPal Email</Label>
          <Input id="edit-paypal-email" type="email" value={paymentData.paypal_email || ''} onChange={(e) => setPaymentData({...paymentData, paypal_email: e.target.value})} className="input-dark" placeholder="your@paypal.com" data-testid="edit-paypal-email-input" />
        </div>
      )}
      {paymentMethod === 'zelle' && (
        <div className="space-y-2">
          <Label htmlFor="edit-zelle-email">Zelle Email</Label>
          <Input id="edit-zelle-email" type="email" value={paymentData.zelle_email || ''} onChange={(e) => setPaymentData({...paymentData, zelle_email: e.target.value})} className="input-dark" placeholder="your@email.com" data-testid="edit-zelle-email-input" />
        </div>
      )}
      {paymentMethod === 'bank_transfer' && (
        <div className="space-y-2">
          <Label htmlFor="edit-bank-name">Bank Name</Label>
          <Input id="edit-bank-name" value={paymentData.bank_name || ''} onChange={(e) => setPaymentData({...paymentData, bank_name: e.target.value})} className="input-dark" placeholder="e.g. Chase" data-testid="edit-bank-name-input" />
          <Label htmlFor="edit-bank-acc">Account Holder Name</Label>
          <Input id="edit-bank-acc" value={paymentData.bank_account_name || ''} onChange={(e) => setPaymentData({...paymentData, bank_account_name: e.target.value})} className="input-dark" placeholder="Full name" data-testid="edit-bank-acc-input" />
          <Label htmlFor="edit-bank-num">Account Number</Label>
          <Input id="edit-bank-num" value={paymentData.bank_account_number || ''} onChange={(e) => setPaymentData({...paymentData, bank_account_number: e.target.value})} className="input-dark" placeholder="Account number" data-testid="edit-bank-num-input" />
          <Label htmlFor="edit-bank-routing">Routing Number (optional)</Label>
          <Input id="edit-bank-routing" value={paymentData.bank_routing_number || ''} onChange={(e) => setPaymentData({...paymentData, bank_routing_number: e.target.value})} className="input-dark" placeholder="Routing number" data-testid="edit-bank-routing-input" />
        </div>
      )}
      {paymentMethod && (
        <Button onClick={handleSavePaymentMethod} disabled={savingPayment} size="sm" className="mt-3 bg-lime text-black hover:bg-lime/90" data-testid="edit-save-payment-btn">
          {savingPayment ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
          Save Payment Method
        </Button>
      )}
    </div>
  );
}

export function ProfileEditDialog({
  open, onOpenChange, profile, profileForm, setProfileForm,
  handleSaveProfile, savingProfile, handleGenreToggle,
  handleRequestVerification, requestingVerification,
  connectStatus, connectLoading, handleConnectBank, handleDisconnectBank, disconnecting,
  paymentMethod, setPaymentMethod, paymentData, setPaymentData, handleSavePaymentMethod, savingPayment
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-[#262626] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>Update your artist profile information</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <VerificationBadge profile={profile} handleRequestVerification={handleRequestVerification} requestingVerification={requestingVerification} />

          <div className="space-y-2">
            <Label htmlFor="edit-bio">Bio</Label>
            <Textarea id="edit-bio" value={profileForm.bio} onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })} className="input-dark min-h-[100px]" data-testid="edit-bio-input" />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-price">Price (USD)</Label>
              <Input id="edit-price" type="number" step="0.01" value={profileForm.price} onChange={(e) => setProfileForm({ ...profileForm, price: e.target.value })} className="input-dark" data-testid="edit-price-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-delivery">Delivery Days</Label>
              <Input id="edit-delivery" type="number" value={profileForm.delivery_days} onChange={(e) => setProfileForm({ ...profileForm, delivery_days: e.target.value })} className="input-dark" data-testid="edit-delivery-input" />
            </div>
          </div>
          
          <ProfilePhotoSection profileForm={profileForm} setProfileForm={setProfileForm} />
          
          <div className="space-y-2">
            <Label htmlFor="edit-spotify">Spotify URL</Label>
            <Input id="edit-spotify" value={profileForm.spotify_url} onChange={(e) => setProfileForm({ ...profileForm, spotify_url: e.target.value })} className="input-dark" data-testid="edit-spotify-input" />
          </div>

          <div className="space-y-2">
            <Label>Genres</Label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_GENRES.map(genre => (
                <button key={genre.value} type="button" onClick={() => handleGenreToggle(genre.value)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${(profileForm.genres || []).includes(genre.value) ? 'bg-lime text-black border-lime' : 'bg-transparent text-[#A1A1AA] border-[#404040] hover:border-lime'}`} data-testid={`edit-genre-${genre.value}`}>
                  {genre.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#666]">Select one or more genres</p>
          </div>

          <div className="pt-4 border-t border-[#262626]">
            <Label className="mb-3 block font-semibold">Bank Account</Label>
            <BankSection connectStatus={connectStatus} connectLoading={connectLoading} handleConnectBank={handleConnectBank} handleDisconnectBank={handleDisconnectBank} disconnecting={disconnecting} />
          </div>

          <PaymentMethodSection paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} paymentData={paymentData} setPaymentData={setPaymentData} handleSavePaymentMethod={handleSavePaymentMethod} savingPayment={savingPayment} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button onClick={handleSaveProfile} disabled={savingProfile} className="btn-primary" data-testid="save-profile-btn">
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

